import { isProgressiveH264FrameFromMp4 } from '../h264/isProgressiveH264FrameFromMp4';
import type { EncodedAudioData, EncodedAudioFrame, EncodedData, EncodedVideoData, EncodedVideoFrame } from '../mpegts/demuxerTypes';
import type { Logger } from '../types/logger';
import { FRAC_ZERO, type Fraction } from '../util/fractionType';
import { addFractions, compareFractions, frac, maxFractions, simplifyFraction } from '../util/integerMath';
import { invariant } from '../util/invariant';
import { safeArrayAccess } from '../util/safeArrayAccess';
import { buildChunkIndexes } from './buildChunkIndexes';
import { buildSampleOffsets } from './buildSampleOffsets';
import { CompositionTimeHelper } from './compositionTimeHelper';
import { KeyframeHelper } from './keyframeHelper';
import type { Mp4AudioTrack, Mp4Info, Mp4VideoTrack, Sample, VirtualSegment } from './mp4Types';
import type { ChunkLargeOffsetBox, ChunkOffsetBox, CompactSampleSizeBox, CompositionOffsetBox, Mp4Box, SampleSizeBox, SampleToChunkBox, SyncSampleBox, TimeToSampleBox } from './parser';
import { SampleDurationHelper } from './sampleDurationHelper';
import { SampleSizeHelper } from './sampleSizeHelper';

export function populateVirtualSegments(
  virtualSegmentUrl: string,
  virtualSegments: VirtualSegment[] | null,
  // null if we have a video track
  audioTrackIndex: number | null,
  mediaStartPtsOffset: number,
  timescale: number,
  stblBox: Mp4Box,
): VirtualSegment[] {
  const isVideo = audioTrackIndex === null;
  // True only for the audio track that seeds the segment grid of a file with no video
  // track at all: an audio+video file's audio track dispatches into the grid the video
  // track already seeded, and never reaches this branch.
  const isFirstTrackOfAudioOnlyFile = !isVideo && virtualSegments === null;
  const stszBox = stblBox.children.find(b => b.type === 'stsz') as SampleSizeBox | undefined;
  const stz2Box = stblBox.children.find(b => b.type === 'stz2') as CompactSampleSizeBox | undefined;

  let sampleCount: number;
  let sampleSizeHelper: SampleSizeHelper;
  if (stszBox) {
    sampleCount = stszBox.sample_count;
    sampleSizeHelper = new SampleSizeHelper(stszBox.sample_size, stszBox.sample_sizes);
  } else if (stz2Box) {
    sampleCount = stz2Box.sample_count;
    sampleSizeHelper = new SampleSizeHelper(0, stz2Box.sample_sizes);
  } else {
    throw new Error('Neither stsz nor stz2 box was found');
  }

  const stscBox = stblBox.children.find(b => b.type === 'stsc') as SampleToChunkBox | undefined;
  invariant(stscBox, 'Missing stsc box');
  const chunkIndexes = buildChunkIndexes(stscBox.first_chunk, stscBox.samples_per_chunk, sampleCount);

  const stcoBox = stblBox.children.find(b => b.type === 'stco') as ChunkOffsetBox | undefined;
  const co64Box = stblBox.children.find(b => b.type === 'co64') as ChunkLargeOffsetBox | undefined;
  let chunkOffsets: number[];
  if (stcoBox) {
    chunkOffsets = stcoBox.chunk_offsets;
  } else if (co64Box) {
    chunkOffsets = co64Box.chunk_offsets;
  } else {
    throw new Error('Neither stco nor co64 box was found');
  }

  const sampleOffsets = buildSampleOffsets(sampleSizeHelper, chunkIndexes, chunkOffsets);


  const sttsBox = stblBox.children.find(b => b.type === 'stts') as TimeToSampleBox | undefined;
  invariant(sttsBox, 'Missing stts box');

  const cttsBox = stblBox.children.find(b => b.type === 'ctts') as CompositionOffsetBox | undefined;

  const videoSampleDuration = isVideo ? getVideoSampleDuration(sttsBox) : 0;
  const sampleDurationHelper = new SampleDurationHelper(sttsBox.sample_count, sttsBox.sample_delta);

  // Unlike createEmptyVirtualSegments() that can use a generated sync sample array,
  // the keyframe helper needs the original sync sample array
  const keyframeHelper = new KeyframeHelper((stblBox.children.find(b => b.type === 'stss') as SyncSampleBox | undefined)?.sample_number);

  const compositionTimeHelper = new CompositionTimeHelper(cttsBox?.sample_counts ?? [], cttsBox?.sample_offsets ?? []);
  const samples: Sample[] = [];
  let currentDts = -mediaStartPtsOffset;

  for (let sampleIndex = 0; sampleIndex < sampleCount; sampleIndex++) {
    let sampleDuration = isVideo ? videoSampleDuration : sampleDurationHelper.getNextSampleDuration();
    const dts = currentDts;
    currentDts += sampleDuration;
    const pts = dts + compositionTimeHelper.getNextCompositionOffset();
    const keyframe = keyframeHelper.isKeyframe(sampleIndex);
    const size = sampleSizeHelper.getSampleSize(sampleIndex);
    const offset = safeArrayAccess(sampleOffsets, sampleIndex);

    const sample: Sample = {
      dts,
      pts,
      offset,
      size,
      duration: sampleDuration,
      keyframe,
    };

    samples.push(sample);
  }

  // segmentedPacketProvider.ts's analyzeSegment() treats the first AAC access unit of an
  // audio-only file as decoder warm-up data with no presentable output of its own, and
  // reports the real playback timeline as starting at the second sample instead. Segment
  // boundaries have to be measured from that same reference point, or a declared segment
  // interval can never match the real decoded PTS range it is compared against.
  const firstPresentedSampleIndex = (isFirstTrackOfAudioOnlyFile && samples.length > 1) ? 1 : 0;
  const firstDts = safeArrayAccess(samples, firstPresentedSampleIndex).dts;

  // if we don't already have virtual segments, we create some based on the sync samples
  // which represent positions it is safe to start decoding from
  if (virtualSegments === null) {
    const syncSamples = getSyncSamples(stblBox, isVideo, sampleCount, timescale);
    // Segment boundaries are derived from the real per-sample dts/duration computed above
    // (which come from the stts box), not from an assumed constant sample duration. Audio
    // sample durations are not guaranteed to be uniform (encoder priming/padding, resampling),
    // so deriving boundaries from anything other than the actual samples would let the
    // reported segment time interval drift away from the real packet timestamps.
    virtualSegments = createEmptyVirtualSegments(syncSamples, samples, firstDts, timescale);
  }

  // Finally, let's put the samples into the virtual segments
  dispatchSamplesIntoVirtualSegments(virtualSegmentUrl, virtualSegments, samples, firstDts, timescale, audioTrackIndex);

  return virtualSegments;
}


export function dispatchSamplesIntoVirtualSegments(
  url: string,
  virtualSegments: VirtualSegment[],
  samples: Sample[],
  // the dts of the sample that defines t=0 on the segment timeline; see populateVirtualSegments()
  firstDts: number,
  timescale: number,
  // null if we have a vide track
  audioTrackIndex: number | null,
): void {
  let currentVirtualSegmentIndex = 0;
  let currentVirtualSegment = safeArrayAccess(virtualSegments, currentVirtualSegmentIndex);
  let sampleArray: Sample[];

  let byteSourceIndex = currentVirtualSegment.byteSources.findIndex(src => src.url === url);
  if (byteSourceIndex === -1) {
    byteSourceIndex = currentVirtualSegment.byteSources.length;
    currentVirtualSegment.byteSources.push({
      url,
      inclusiveByteRangeStart: -1,
      exclusiveByteRangeEnd: -1,
    });
  }
  let byteSource = safeArrayAccess(currentVirtualSegment.byteSources, byteSourceIndex);

  if (audioTrackIndex === null) {
    if (currentVirtualSegment.videoData === undefined) {
      currentVirtualSegment.videoData = {
        byteSourceIndex,
        samples: [],
      };
    }
    sampleArray = currentVirtualSegment.videoData.samples;
  } else {
    currentVirtualSegment.audioData[audioTrackIndex] = {
      byteSourceIndex,
      samples: [],
    };
    sampleArray = safeArrayAccess(currentVirtualSegment.audioData, audioTrackIndex).samples;
  }

  // Since the virtual segment in/out points are built from the video sample
  // index in decode order multiplied by the frame duration it means that when
  // deciding which samples goes into which virtual segment we need to subtract
  // the first DTS since the first segment in-point will be zero.
  //
  // Since we are considering this per-stream it means that audio may get
  // shifted with a different amount than the video stream. However, this is
  // not a problem since we allow audio to not align perfectly with the segment
  // boundaries and we are not modifying the actual pts/dts values of the
  // packets. We only use this to determine which sample goes into which
  // segment.
  for (let sampleIndex = 0; sampleIndex < samples.length; sampleIndex++) {
    const sample = safeArrayAccess(samples, sampleIndex);
    const sampleStartTimeInSeconds = frac(sample.dts - firstDts, timescale);

    if (compareFractions(sampleStartTimeInSeconds, currentVirtualSegment.exclusiveEndInSeconds) >= 0 && currentVirtualSegmentIndex < (virtualSegments.length - 1)) {
      // The current sample does not start before the end of the current segment,
      // time to move on to the next segment
      currentVirtualSegmentIndex++;

      currentVirtualSegment = safeArrayAccess(virtualSegments, currentVirtualSegmentIndex);

      byteSourceIndex = currentVirtualSegment.byteSources.findIndex(src => src.url === url);
      if (byteSourceIndex === -1) {
        byteSourceIndex = currentVirtualSegment.byteSources.length;
        currentVirtualSegment.byteSources.push({
          url,
          inclusiveByteRangeStart: -1,
          exclusiveByteRangeEnd: -1,
        });
      }
      byteSource = safeArrayAccess(currentVirtualSegment.byteSources, byteSourceIndex);

      if (audioTrackIndex === null) {
        if (currentVirtualSegment.videoData === undefined) {
          currentVirtualSegment.videoData = {
            byteSourceIndex,
            samples: [],
          };
        }
        sampleArray = currentVirtualSegment.videoData.samples;
      } else {
        currentVirtualSegment.audioData[audioTrackIndex] = {
          byteSourceIndex,
          samples: [],
        };
        sampleArray = safeArrayAccess(currentVirtualSegment.audioData, audioTrackIndex).samples;
      }
    }

    // Let's push the sample and update the virtual segment byte range to make sure it includes this sample's bytes
    sampleArray.push(sample);
    if (byteSource.inclusiveByteRangeStart === -1 || sample.offset < byteSource.inclusiveByteRangeStart) {
      byteSource.inclusiveByteRangeStart = sample.offset;
    }
    const sampleEnd = sample.offset + sample.size;
    if (byteSource.exclusiveByteRangeEnd === -1 || sampleEnd > byteSource.exclusiveByteRangeEnd) {
      byteSource.exclusiveByteRangeEnd = sampleEnd;
    }
  }
}


/**
 * Gets the sync sample array if present. If not, all samples are sync samples
 * and we generate an array with some arbitrary grouping.
 *
 * Note that the sync samples indexes are numbered from 1.
 */
function getSyncSamples(
  stblBox: Mp4Box,
  isVideo: boolean,
  sampleCount: number,
  timescale: number,
): number[] {
  const syncSamples = (stblBox.children.find(b => b.type === 'stss') as SyncSampleBox | undefined)?.sample_number;
  if (syncSamples !== undefined) {
    return syncSamples;
  }

  // If we don't have a sync sample box, every sample is a sync one. In that case, we create some
  // arbitrary groups of 10 video I-frames or about 10 seconds worth of audio AAC frames of 1024
  // samples
  const sampleGroupSize = isVideo ? 10 : Math.round((timescale * 10) / 1024);
  return makeSyncSampleArray(sampleGroupSize, sampleCount);
}


/**
 * Based on the given sync samples, this function creates an array of
 * empty virtual segments that are only defined by their start and end
 * time positions. The boundaries are derived from the real dts/duration of
 * the given samples (as opposed to an assumed constant sample duration) so
 * that they cannot drift away from the actual packet timestamps dispatched
 * into these segments afterwards.
 */
export function createEmptyVirtualSegments(
  syncSamples: number[],
  samples: Sample[],
  // the dts of the sample that defines t=0 on the segment timeline; see populateVirtualSegments()
  firstDts: number,
  timescale: number,
): VirtualSegment[] {
  const virtualSegments: VirtualSegment[] = [];

  if (syncSamples.length === 0) {
    return virtualSegments;
  }

  const sampleStartInSeconds = (sampleIndex: number): Fraction => simplifyFraction(frac(safeArrayAccess(samples, sampleIndex).dts - firstDts, timescale));

  for (let i = 0; i < syncSamples.length; i++) {
    // We normalize the sample indexes to start at 0
    const inclusiveStartSample = safeArrayAccess(syncSamples, i) - 1;

    // Exclude the init packet when setting virtual segment boundaries since
    // the virtual segment should start at zero.
    const inclusiveStartInSeconds = maxFractions(sampleStartInSeconds(inclusiveStartSample), FRAC_ZERO);

    const isLastSegment = i === (syncSamples.length - 1);
    const exclusiveEndSample = isLastSegment ? samples.length : safeArrayAccess(syncSamples, i + 1) - 1;

    const exclusiveEndInSeconds = isLastSegment
      ? addFractions(sampleStartInSeconds(exclusiveEndSample - 1), frac(safeArrayAccess(samples, exclusiveEndSample - 1).duration, timescale))
      : sampleStartInSeconds(exclusiveEndSample);

    virtualSegments.push({
      inclusiveStartInSeconds,
      exclusiveEndInSeconds,
      videoData: undefined,
      audioData: [],
      byteSources: [],
    });
  }

  return virtualSegments;
}


/**
 * Since we don't support variable framerates, we expect all samples to
 * have the same duration, except maybe the last one.
 */
function getVideoSampleDuration(sttsBox: TimeToSampleBox) {
  const duration = safeArrayAccess(sttsBox.sample_delta, 0);
  for (let i = 1; i < sttsBox.sample_delta.length - 1; i++) {
    if (safeArrayAccess(sttsBox.sample_delta, i) !== duration) {
      throw new Error(`Variable framerate: sample_delta[${i}]=${safeArrayAccess(sttsBox.sample_delta, i)} not the same as sample_delta[0]=${duration}`);
    }
  }
  return duration;
}


export function makeSyncSampleArray(groupSize: number, nSamples: number): number[] {
  if (nSamples === 0) {
    return [];
  }

  // Samples are numbered from 1
  const firstSample = 1;
  const lastSample = nSamples;
  const syncSamples: number[] = [];
  for (let sample = firstSample; sample <= lastSample; sample += groupSize) {
    syncSamples.push(sample);
  }
  return syncSamples;
}


/**
 * Returns the index of the virtual segment containing the given position of -1 if no segment contains it.
 */
export function findVirtualSegmentIndex(
  position: Fraction,
  virtualSegments: VirtualSegment[],
): number {
  if (virtualSegments.length === 0) {
    // No segments at all
    return -1;
  }

  let first = 0;
  if (compareFractions(position, safeArrayAccess(virtualSegments, first).inclusiveStartInSeconds) < 0) {
    // The position is before the inclusive start of the first segment
    return -1;
  }

  let last = virtualSegments.length - 1;
  if (compareFractions(position, safeArrayAccess(virtualSegments, last).exclusiveEndInSeconds) >= 0) {
    // The position is >= the exclusive end of the last segment
    return -1;
  }

  // Now we know that that there is a segment that contains the given position
  while (first < last) {
    const middle = Math.round((first + last) / 2.0);
    const segment = safeArrayAccess(virtualSegments, middle);
    if (compareFractions(position, segment.inclusiveStartInSeconds) < 0) {
      last = middle - 1;
      continue;
    }

    if (compareFractions(position, segment.exclusiveEndInSeconds) >= 0) {
      first = middle + 1;
      continue;
    }

    return middle;
  }

  invariant(first === last, 'Should have first === last');
  const segment = safeArrayAccess(virtualSegments, first);
  invariant(compareFractions(position, segment.inclusiveStartInSeconds) >= 0, 'Position should be inside range');
  invariant(compareFractions(position, segment.exclusiveEndInSeconds) < 0, 'Position should be inside range');
  return first;
}


function buildEncodedVideoData(
  logger: Logger,
  byteOffset: number,
  bytes: Uint8Array,
  videoSamples: Sample[],
  videoTrack: Mp4VideoTrack,
): EncodedVideoData {
  const frames = videoSamples.map<EncodedVideoFrame>(sample => ({
    pts: sample.pts,
    dts: sample.dts,
    duration: sample.duration,
    keyframe: sample.keyframe,
    data: bytes.slice(sample.offset - byteOffset, sample.offset - byteOffset + sample.size),
  }));

  let progressive: boolean;
  const progressiveStatus = videoTrack.progressiveStatus;
  switch (progressiveStatus) {
    case 'progressive': progressive = true; break;
    case 'interlaced': progressive = false; break;
    case 'need-to-inspect-nal_units': {
      const frame = frames[0];
      invariant(frame, 'Should have one video frame');
      invariant(videoTrack.frameInfo, 'Should have frame info');
      progressive = isProgressiveH264FrameFromMp4(logger, videoTrack.frameInfo, videoTrack.nalLengthSize, frame.data);
      // Let's update the mp4 info to avoid having to redo this analysis later
      videoTrack.progressiveStatus = progressive ? 'progressive' : 'interlaced';
      break;
    }
    default: progressiveStatus satisfies never; throw new Error(`Unsupported progressive status: ${progressiveStatus}`);
  }

  return {
    codec: videoTrack.codecString,
    description: videoTrack.avcC,
    width: videoTrack.width,
    height: videoTrack.height,
    progressive,
    frameDuration: videoTrack.frameDuration,
    pixelAspectRatio: videoTrack.pixelAspectRatio,
    frames,
    colorSpace: null,
    timescale: videoTrack.timescale,
    epsilon: FRAC_ZERO,
    exactFrameDuration: true,
  };
}


function buildEncodedAudioData(
  byteOffset: number,
  bytes: Uint8Array,
  audioSamples: Sample[],
  audioTrack: Mp4AudioTrack,
): EncodedAudioData {

  return {
    codec: 'mp4a.40.2',
    frames: audioSamples.map<EncodedAudioFrame>(sample => ({
      pts: sample.pts,
      dts: sample.dts,
      duration: sample.duration,
      keyframe: sample.keyframe,
      data: bytes.slice(sample.offset - byteOffset, sample.offset - byteOffset + sample.size),
    })),
    timescale: audioTrack.timescale,
    numberOfChannels: audioTrack.channelCount,
    sampleRateInHz: audioTrack.sampleRate,
    epsilon: FRAC_ZERO,
    codecSpecificData: audioTrack.codecSpecificData,
  };
}


export function buildEncodedDataFromVirtualSegment(
  logger: Logger,
  byteArrays: Uint8Array[],
  virtualSegment: VirtualSegment,
  mp4Info: Mp4Info,
): EncodedData {
  let videoByteSourceIndex = (virtualSegment.videoData === undefined) ? -1 : virtualSegment.videoData.byteSourceIndex;
  const encodedVideoData = (virtualSegment.videoData === undefined)
    ? undefined
    : buildEncodedVideoData(logger, safeArrayAccess(virtualSegment.byteSources, videoByteSourceIndex).inclusiveByteRangeStart, byteArrays[videoByteSourceIndex]!, virtualSegment.videoData.samples, safeArrayAccess(mp4Info.videoTracks, 0));

  const encodedAudioData: EncodedAudioData[] = [];
  for (let i = 0; i < mp4Info.audioTracks.length; i++) {
    if (i < virtualSegment.audioData.length) {
      const audioByteSourceIndex = safeArrayAccess(virtualSegment.audioData, i).byteSourceIndex;
      encodedAudioData.push(buildEncodedAudioData(safeArrayAccess(virtualSegment.byteSources, audioByteSourceIndex).inclusiveByteRangeStart, byteArrays[audioByteSourceIndex]!, safeArrayAccess(virtualSegment.audioData, i).samples, safeArrayAccess(mp4Info.audioTracks, i)));
    } else {
      // If we the last sync video sample is the last video sample, it is possible to end up with
      // the last virtual segment containing no audio at all if the media ends like this:
      //
      //                    ...   [ last sync video sample ]
      //    ...   [ last audio sample ]
      //
      // If that happens, there is no audio data to access in the virtual segment. Since segments are expected
      // to have a consistent video/audio layout, we generate EncodedAudioData objects that contain the correct
      // information but no samples.
      encodedAudioData.push(buildEncodedAudioData(0, new Uint8Array([]), [], safeArrayAccess(mp4Info.audioTracks, i)));
    }
  }
  return {
    encodedVideoData,
    encodedAudioData,
  };
}
